import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Trash2, Shield, ShieldCheck, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";

const ROLE_META = {
  super_admin:       { label: "Platform Admin",           tone: "border-primary text-primary bg-red-50" },
  company_admin:     { label: "Company Admin",            tone: "border-primary text-primary bg-red-50" },
  company_executive: { label: "Company Executive",        tone: "border-sky-500 text-sky-800 bg-sky-50" },
  branch_admin:      { label: "Branch Admin",             tone: "border-amber-500 text-amber-800 bg-amber-50" },
  branch_user:       { label: "Branch User",              tone: "border-slate-400 text-slate-700 bg-slate-50" },
  delivery_staff:    { label: "Delivery Staff (Runner)",  tone: "border-emerald-500 text-emerald-800 bg-emerald-50" },
  route_moderator:   { label: "Route Moderator",          tone: "border-indigo-500 text-indigo-800 bg-indigo-50" },
};

const canAssignRolesFor = (role) => {
  if (role === "super_admin")       return ["company_admin", "company_executive", "branch_admin", "branch_user", "delivery_staff", "route_moderator"];
  if (role === "company_admin")     return ["company_executive", "branch_admin", "branch_user", "delivery_staff", "route_moderator"];
  // Feb 13, 2026 · Company Executive can manage every sub-admin role — NEVER admin roles.
  if (role === "company_executive") return ["branch_admin", "branch_user", "delivery_staff", "route_moderator"];
  if (role === "branch_admin")      return ["branch_user", "delivery_staff"];
  return [];
};

const requiresBranch = (role) => role === "branch_admin" || role === "branch_user" || role === "delivery_staff";

export default function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", name: "", email: "", password: "", role: "", branch_id: "", tenant_id: "", currentRole: "", allowed_branch_ids: [], allowed_route_ids: [], status: "active" });
  const allowedRoles = canAssignRolesFor(user?.role);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: allowedRoles[0] || "branch_user", branch_id: "", allowed_branch_ids: [] });

  const load = async () => {
    const [u, b] = await Promise.all([api.get("/users"), api.get("/branches")]);
    setItems(u.data);
    setBranches(b.data);
    if (user?.role === "super_admin") {
      try {
        const { data } = await api.get("/tenants");
        setTenants(data);
      } catch { /* ignore */ }
    }
    try {
      const { data } = await api.get("/routes", { params: { status: "active" } });
      setRoutes(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (requiresBranch(form.role) && !form.branch_id && user?.role !== "branch_admin") {
      return toast.error("Branch is required for this role");
    }
    try {
      const payload = { ...form, branch_id: form.branch_id || null };
      // Only send allowed_branch_ids for company_admin (and only super_admin can set it)
      if (form.role === "company_admin" && user?.role === "super_admin") {
        payload.allowed_branch_ids = form.allowed_branch_ids && form.allowed_branch_ids.length ? form.allowed_branch_ids : null;
      } else {
        delete payload.allowed_branch_ids;
      }
      // Feb 10, 2026 — Route Moderator scope
      if (form.role === "route_moderator") {
        payload.allowed_route_ids = form.allowed_route_ids && form.allowed_route_ids.length ? form.allowed_route_ids : [];
      } else {
        delete payload.allowed_route_ids;
      }
      await api.post("/users", payload);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: allowedRoles[0] || "branch_user", branch_id: "", allowed_branch_ids: [], allowed_route_ids: [] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (u.id === user?.id) return toast.error("You cannot delete yourself");
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openEdit = (u) => {
    setEditForm({
      id: u.id,
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role,
      currentRole: u.role,
      branch_id: u.branch_id || "",
      tenant_id: u.tenant_id || "",
      allowed_branch_ids: Array.isArray(u.allowed_branch_ids) ? u.allowed_branch_ids : [],
      allowed_route_ids: Array.isArray(u.allowed_route_ids) ? u.allowed_route_ids : [],
      status: u.status === "inactive" ? "inactive" : "active",
      currentStatus: u.status === "inactive" ? "inactive" : "active",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editForm.name) payload.name = editForm.name;
      // Feb 12, 2026 — email & password are NOT editable from this screen.
      // Feb 13, 2026 — send status only when it changed to avoid noisy audit rows.
      if (editForm.status && editForm.status !== editForm.currentStatus) payload.status = editForm.status;
      // Role change (only super_admin can change to super_admin-adjacent roles, others restricted)
      const canChangeRole = user?.role === "super_admin" && editForm.currentRole !== "super_admin";
      if (canChangeRole && editForm.role && editForm.role !== editForm.currentRole) {
        payload.role = editForm.role;
      }
      // Branch only relevant if role needs branch
      const finalRole = payload.role || editForm.currentRole;
      if (user?.role !== "branch_admin") {
        if (requiresBranch(finalRole)) {
          if (!editForm.branch_id) return toast.error("Branch is required for this role");
          if (editForm.branch_id !== "__existing__") payload.branch_id = editForm.branch_id;
        } else if (finalRole === "company_admin") {
          // company_admin has no branch
          payload.branch_id = null;
        }
      }
      // Feb 10, 2026 — Route Moderator scope on edit (send whenever the role is route_moderator)
      if (finalRole === "route_moderator") {
        payload.allowed_route_ids = editForm.allowed_route_ids && editForm.allowed_route_ids.length ? editForm.allowed_route_ids : [];
      }
      if (Object.keys(payload).length === 0) return toast.error("Nothing to update");
      await api.patch(`/users/${editForm.id}`, payload);
      toast.success("User updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const canDelete = (target) => {
    if (!user) return false;
    if (target.id === user.id) return false;
    if (target.role === "super_admin") return false;
    if (user.role === "super_admin") return true;
    if (user.role === "company_admin") return ["branch_admin", "branch_user", "delivery_staff", "company_executive", "route_moderator"].includes(target.role) && target.tenant_id === user.tenant_id;
    if (user.role === "company_executive") return ["branch_admin", "branch_user", "delivery_staff", "route_moderator"].includes(target.role) && target.tenant_id === user.tenant_id;
    if (user.role === "branch_admin")  return ["branch_user", "delivery_staff"].includes(target.role) && target.branch_id === user.branch_id;
    return false;
  };

  const canEdit = (target) => {
    if (!user) return false;
    if (target.id === user.id) return true; // can edit self
    if (target.role === "super_admin" && user.role !== "super_admin") return false;
    if (user.role === "super_admin") return true;
    if (user.role === "company_admin") return ["company_admin", "branch_admin", "branch_user", "delivery_staff", "company_executive", "route_moderator"].includes(target.role) && target.tenant_id === user.tenant_id;
    if (user.role === "company_executive") return ["branch_admin", "branch_user", "delivery_staff", "route_moderator"].includes(target.role) && target.tenant_id === user.tenant_id;
    if (user.role === "branch_admin")  return ["branch_user", "delivery_staff"].includes(target.role) && target.branch_id === user.branch_id;
    return false;
  };

  // Which roles can the actor change a given target TO?
  const roleOptionsForEdit = () => {
    if (editForm.currentRole === "super_admin") return null;  // never demote super_admin here
    if (user?.role === "super_admin") return ["company_admin", "branch_admin", "branch_user", "delivery_staff", "route_moderator"];
    if (user?.role === "company_admin") return ["branch_admin", "branch_user", "delivery_staff", "route_moderator", "company_executive"];
    if (user?.role === "company_executive") return ["branch_admin", "branch_user", "delivery_staff", "route_moderator"];
    return null;
  };

  const editTenantName = (() => {
    if (!editForm.tenant_id) return null;
    const t = tenants.find((x) => x.id === editForm.tenant_id);
    return t?.name || null;
  })();

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><UsersIcon size={12} /> Team & access · Role hierarchy</div>
          <h1 className="text-4xl font-display mt-1">Users.</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.role === "branch_admin"
              ? "You can add / remove branch users for your branch, and reset their credentials."
              : user?.role === "company_admin"
                ? "You can add / remove branch admins and branch users across your tenant, and reset their credentials."
                : "Platform-wide user management. You can manage every account, change roles and branch assignments, and reset credentials."}
          </p>
        </div>
        {allowedRoles.length > 0 && (
          <Button data-testid="user-new-btn" onClick={() => setOpen(true)} className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
            <Plus size={14} className="mr-2" /> Add user
          </Button>
        )}
      </header>

      <div className="border border-border bg-card rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline">
              <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Branch</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-0">
                <EmptyState icon={ShieldCheck} title="No users yet" subtitle="Add your first team member to get started." testid="empty-users" />
              </td></tr>
            )}
            {items.map((u) => {
              const meta = ROLE_META[u.role] || { label: u.role, tone: "border-border" };
              return (
                <tr key={u.id} className="border-b border-border hover:bg-muted/40" data-testid={`user-row-${u.id}`}>
                  <td className="p-4 font-semibold">{u.name}</td>
                  <td className="p-4 mono text-xs">{u.email}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded ${meta.tone}`}>
                      {u.role === "super_admin" && <Shield size={10} />}
                      {meta.label}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-muted-foreground">{branches.find((b) => b.id === u.branch_id)?.name || "—"}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      {canEdit(u) && (
                        <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-semibold"
                          title="Edit user">
                          <KeyRound size={12} /> Edit
                        </button>
                      )}
                      {canDelete(u) && (
                        <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-semibold"
                          title="Delete user">
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                      {!canEdit(u) && !canDelete(u) && (
                        <span className="text-[10px] text-muted-foreground italic">read-only</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-md" aria-describedby="add-user-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add User</DialogTitle>
            <p id="add-user-desc" className="text-xs text-muted-foreground">Create a team member. Roles available depend on your permissions.</p>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <F label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} t="usr-name" required />
            <F label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} t="usr-email" required type="email" />
            <F label="Password" v={form.password} onChange={(v) => setForm({ ...form, password: v })} t="usr-pw" required type="password" />
            <label className="block">
              <span className="overline">Role</span>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="usr-role" className="rounded-md border-input mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedRoles.map((r) => (<SelectItem key={r} value={r}>{ROLE_META[r]?.label || r}</SelectItem>))}
                </SelectContent>
              </Select>
            </label>
            {user?.role !== "branch_admin" && requiresBranch(form.role) && (
              <label className="block">
                <span className="overline">Branch <span className="text-primary">*</span></span>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="usr-branch" className="rounded-md border-input mt-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </label>
            )}
            {user?.role === "branch_admin" && (
              <div className="text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2">
                New user will be added to your branch: <b>{branches.find((b) => b.id === user.branch_id)?.name || "your branch"}</b>
              </div>
            )}
            {/* Data Scope for Company Admin — super_admin only */}
            {user?.role === "super_admin" && form.role === "company_admin" && (
              <BranchScopePicker
                testidPrefix="usr-scope"
                branches={branches}
                value={form.allowed_branch_ids || []}
                onChange={(v) => setForm({ ...form, allowed_branch_ids: v })}
              />
            )}
            {/* Route Moderator scope — assigned routes multi-select */}
            {form.role === "route_moderator" && (
              <label className="block" data-testid="usr-route-scope">
                <span className="overline">Assigned Routes *</span>
                <div className="mt-1 max-h-40 overflow-y-auto border border-input rounded-md p-2 space-y-1 bg-background">
                  {routes.length === 0 && <div className="text-[11px] text-muted-foreground">No active routes yet — create one under Route Master first.</div>}
                  {routes.map((r) => {
                    const checked = (form.allowed_route_ids || []).includes(r.id);
                    return (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" data-testid={`usr-route-${r.id}`} checked={checked}
                          onChange={(e) => {
                            const next = new Set(form.allowed_route_ids || []);
                            if (e.target.checked) next.add(r.id); else next.delete(r.id);
                            setForm({ ...form, allowed_route_ids: Array.from(next) });
                          }}
                          className="h-4 w-4 accent-[#BE1E1E]" />
                        <span>{r.name} <span className="mono text-muted-foreground">· {r.code}</span></span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Moderator will see only parcels on these routes.</div>
              </label>
            )}
            <Button data-testid="usr-submit" type="submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">Create user</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog — with Role dropdown + contextual branch selector */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-md max-w-md" aria-describedby="edit-user-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><KeyRound size={18} /> Edit User</DialogTitle>
            <p id="edit-user-desc" className="text-xs text-muted-foreground">
              Update name, email, or reset password. {user?.role === "super_admin" && "Change role & branch as needed. "}Leave password blank to keep the current one.
            </p>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            {/* Tenant read-only badge for context */}
            {editTenantName && (
              <div className="text-[11px] bg-muted/40 border border-border rounded-md p-2 flex items-center gap-2">
                <span className="overline">Tenant</span>
                <span className="font-semibold">{editTenantName}</span>
                <span className="mono text-[9px] text-muted-foreground ml-auto">Change tenant is not supported — delete & recreate the user.</span>
              </div>
            )}

            <F label="Name" v={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} t="usr-edit-name" />
            <div className="text-[10px] text-muted-foreground -mt-1">Email & password can't be changed from this screen.</div>

            {/* Feb 13, 2026 — Active / Inactive toggle. Inactive users are blocked at login. */}
            {editForm.id !== user?.id && (
              <label className="block">
                <span className="overline">Status</span>
                <Select value={editForm.status || "active"} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="usr-edit-status" className="rounded-md border-input mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active" data-testid="usr-edit-status-active">Active</SelectItem>
                    <SelectItem value="inactive" data-testid="usr-edit-status-inactive">Inactive — block sign-in</SelectItem>
                  </SelectContent>
                </Select>
                {editForm.status === "inactive" && editForm.currentStatus !== "inactive" && (
                  <div className="text-[10px] text-primary mt-1 font-semibold">
                    This user will be signed out on their next request and blocked from logging back in.
                  </div>
                )}
              </label>
            )}

            {/* Role dropdown (super_admin only) */}
            {roleOptionsForEdit() && (
              <label className="block">
                <span className="overline">Role</span>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="usr-edit-role" className="rounded-md border-input mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptionsForEdit().map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_META[r]?.label || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editForm.role !== editForm.currentRole && (
                  <div className="text-[10px] text-primary mt-1 font-semibold">
                    Role will change from {ROLE_META[editForm.currentRole]?.label || editForm.currentRole} → {ROLE_META[editForm.role]?.label || editForm.role}
                  </div>
                )}
              </label>
            )}

            {/* Contextual: Branch selector only when role requires it */}
            {user?.role !== "branch_admin" && requiresBranch(editForm.role) && (
              <label className="block">
                <span className="overline">Branch <span className="text-primary">*</span></span>
                <Select value={editForm.branch_id} onValueChange={(v) => setEditForm({ ...editForm, branch_id: v })}>
                  <SelectTrigger data-testid="usr-edit-branch" className="rounded-md border-input mt-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </label>
            )}

            {editForm.role === "company_admin" && (
              <div className="text-[11px] text-muted-foreground bg-red-50/40 border border-primary/30 rounded-md p-2">
                Company admins are not tied to any branch — they oversee <b>{editTenantName || "the company"}</b> as a whole.
              </div>
            )}

            {/* Data Scope for Company Admin — super_admin only */}
            {user?.role === "super_admin" && editForm.role === "company_admin" && (
              <BranchScopePicker
                testidPrefix="usr-edit-scope"
                branches={branches}
                value={editForm.allowed_branch_ids || []}
                onChange={(v) => setEditForm({ ...editForm, allowed_branch_ids: v })}
              />
            )}
            {/* Route Moderator scope — assigned routes multi-select */}
            {editForm.role === "route_moderator" && (
              <label className="block" data-testid="usr-edit-route-scope">
                <span className="overline">Assigned Routes</span>
                <div className="mt-1 max-h-40 overflow-y-auto border border-input rounded-md p-2 space-y-1 bg-background">
                  {routes.length === 0 && <div className="text-[11px] text-muted-foreground">No active routes.</div>}
                  {routes.map((r) => {
                    const checked = (editForm.allowed_route_ids || []).includes(r.id);
                    return (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" data-testid={`usr-edit-route-${r.id}`} checked={checked}
                          onChange={(e) => {
                            const next = new Set(editForm.allowed_route_ids || []);
                            if (e.target.checked) next.add(r.id); else next.delete(r.id);
                            setEditForm({ ...editForm, allowed_route_ids: Array.from(next) });
                          }}
                          className="h-4 w-4 accent-[#BE1E1E]" />
                        <span>{r.name} <span className="mono text-muted-foreground">· {r.code}</span></span>
                      </label>
                    );
                  })}
                </div>
              </label>
            )}

            <Button data-testid="usr-edit-submit" type="submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">Save changes</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BranchScopePicker({ testidPrefix, branches, value, onChange }) {
  const isAll = !value || value.length === 0;
  const toggle = (id) => {
    const next = new Set(value || []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div className="border border-border rounded-md p-3 bg-muted/20" data-testid={`${testidPrefix}-block`}>
      <div className="overline mb-1">Data Scope</div>
      <div className="flex items-center gap-4 mb-2 text-xs">
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`${testidPrefix}-all`}>
          <input type="radio" checked={isAll} onChange={() => onChange([])} className="accent-[#BE1E1E]" />
          <span className={isAll ? "font-semibold" : ""}>All Branches (default)</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`${testidPrefix}-selected`}>
          <input type="radio" checked={!isAll} onChange={() => onChange(value && value.length ? value : (branches[0] ? [branches[0].id] : []))} className="accent-[#BE1E1E]" />
          <span className={!isAll ? "font-semibold" : ""}>Selected Branches Only</span>
        </label>
      </div>
      {!isAll && (
        <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto pt-2 border-t border-border" data-testid={`${testidPrefix}-list`}>
          {branches.map((b) => (
            <label key={b.id} className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid={`${testidPrefix}-item-${b.id}`}>
              <input type="checkbox" checked={value.includes(b.id)} onChange={() => toggle(b.id)} className="accent-[#BE1E1E]" />
              <span className="truncate">{b.name}</span>
            </label>
          ))}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground mt-2">
        {isAll ? "Company Admin will see data from every branch in the tenant." :
          `Company Admin will be restricted to ${(value || []).length} branch(es).`}
      </div>
    </div>
  );
}

function F({ label, v, onChange, t, type = "text", required }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={t} value={v || ""} onChange={(e) => onChange(e.target.value)} required={required} type={type}
        className="mt-1 rounded-md border-input focus-visible:ring-primary" />
    </label>
  );
}
